import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CloseIcon, PenLineIcon } from "@/components/icons";
import { CheckIcon } from "lucide-react";

export type NamedNumberItem = { name: string; number?: number };

type NamedNumberListEditorProps = {
  value?: NamedNumberItem[];
  onChange: (v: NamedNumberItem[]) => void;
  withNumber?: boolean;
  numberPlaceholder?: string;
};

export const NamedNumberListEditor = ({
  value = [],
  onChange,
  withNumber,
  numberPlaceholder,
}: NamedNumberListEditorProps) => {
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editingNumber, setEditingNumber] = useState("");

  const toItem = (rawName: string, rawNumber: string): NamedNumberItem => {
    if (!withNumber) return { name: rawName.trim() };
    const parsed = parseInt(rawNumber);
    return { name: rawName.trim(), number: isNaN(parsed) ? undefined : parsed };
  };

  const add = () => {
    if (!name.trim()) return;
    onChange([...value, toItem(name, number)]);
    setName("");
    setNumber("");
  };

  const startEdit = (i: number) => {
    setEditingIndex(i);
    setEditingName(value[i].name);
    setEditingNumber(value[i].number?.toString() ?? "");
  };

  const confirmEdit = () => {
    if (editingIndex === null || !editingName.trim()) return;
    const updated = [...value];
    updated[editingIndex] = toItem(editingName, editingNumber);
    onChange(updated);
    setEditingIndex(null);
  };

  const cancelEdit = () => setEditingIndex(null);

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nom"
          className="flex-1"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        {withNumber && (
          <Input
            type="number"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder={numberPlaceholder}
            className="w-36"
          />
        )}
        <Button type="button" variant="outline" onClick={add}>
          Ajouter
        </Button>
      </div>
      {value.length > 0 && (
        <div className="space-y-1">
          {value.map((item, i) => (
            <div key={i} className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-1.5">
              {editingIndex === i ? (
                <>
                  <Input
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    className="h-6 flex-1 text-xs px-1.5 py-0"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        confirmEdit();
                      }
                      if (e.key === "Escape") cancelEdit();
                    }}
                  />
                  {withNumber && (
                    <Input
                      type="number"
                      value={editingNumber}
                      onChange={(e) => setEditingNumber(e.target.value)}
                      className="h-6 w-24 text-xs px-1.5 py-0"
                    />
                  )}
                  <button type="button" onClick={confirmEdit} className="text-green-600 hover:text-green-700 shrink-0">
                    <CheckIcon className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="text-muted-foreground hover:text-foreground shrink-0"
                  >
                    <CloseIcon className="h-3.5 w-3.5" />
                  </button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-xs">
                    {item.name}
                    {withNumber && item.number !== undefined ? ` — ${item.number}` : ""}
                  </span>
                  <button
                    type="button"
                    onClick={() => startEdit(i)}
                    className="text-muted-foreground hover:text-foreground shrink-0"
                  >
                    <PenLineIcon className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onChange(value.filter((_, j) => j !== i))}
                    className="text-muted-foreground hover:text-destructive shrink-0"
                  >
                    <CloseIcon className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
